// src/pages/PricingPage.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
} from "@mui/material";
import { Check, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../config/apiKey.js";
import { useAuth } from "../context/AuthContext";
import IconButton from "@mui/material/IconButton";
import { safeRender } from "../utils/safeRender.js";

export default function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const getDefaultPlans = () => [
    {
      id: "free",
      name: "Free",
      price: 0,
      limits: { dailyPrompts: 10, totalPrompts: 100, dailyImages: 1, dailyVideos: 0, maxUserLogins: 1 },
    },
    {
      id: "basic",
      name: "Basic",
      price: 499,
      limits: { dailyPrompts: 100, totalPrompts: null, dailyImages: 5, dailyVideos: 0, maxUserLogins: 1 },
    },
    {
      id: "pro",
      name: "Pro",
      price: 1499,
      limits: { dailyPrompts: null, totalPrompts: null, dailyImages: 10, dailyVideos: 0, maxUserLogins: 2 },
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: 1999,
      limits: { dailyPrompts: null, totalPrompts: null, dailyImages: 15, dailyVideos: 0, maxUserLogins: 4 },
    },
  ];

  useEffect(() => {
    fetchPlans();
    loadRazorpayScript();
  }, []);

  const loadRazorpayScript = () => {
    if (window.Razorpay) return;
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => console.log("Razorpay script loaded successfully");
    script.onerror = () => console.error("Failed to load Razorpay script");
    document.body.appendChild(script);
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/plans`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.plans)) {
          setPlans(data.plans);
        } else {
          // Fallback to default plans if API fails
          setPlans(getDefaultPlans());
        }
      } else {
        // Fallback to default plans
        setPlans(getDefaultPlans());
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      // Fallback to default plans
      setPlans(getDefaultPlans());
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    // Always check authentication first - redirect to login if not authenticated
    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/pricing", planId } });
      return;
    }

    if (planId === "free") {
      // Free plan - just redirect to dashboard
      navigate("/dashboard");
      return;
    }

    // For paid plans, initiate payment
    try {
      setError(""); // Clear previous errors
      const response = await fetch(`${API_BASE_URL}/api/payment/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ planId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.error || errorData.message || "Failed to create payment order. Please try again.";
        
        // Provide user-friendly message for 503 errors
        if (response.status === 503) {
          errorMessage = "Payment gateway is not configured. Please contact support or try again later.";
          if (errorData.details) {
            console.error("Payment gateway configuration issue:", errorData.details);
          }
        }
        
        setError(errorMessage);
        console.error("Payment order creation failed:", response.status, errorData);
        return;
      }

      const data = await response.json();
      
      // Check if Razorpay script is loaded
      if (!window.Razorpay) {
        // Try to load it again if it's missing
        loadRazorpayScript();
        setError("Payment gateway is loading. Please try again in a few seconds.");
        console.error("Razorpay script not found at payment time.");
        return;
      }

      // Check if we have all required data
      if (!data.keyId || !data.orderId || !data.amount) {
        setError("Invalid payment data received. Please try again.");
        console.error("Missing payment data:", data);
        return;
      }

      // Initialize Razorpay payment
      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency || "INR",
        name: "exivis",
        description: `Upgrade to ${planId} plan`,
        order_id: data.orderId,
        handler: async function (paymentResponse) {
          try {
            // Verify payment
            const verifyResponse = await fetch(`${API_BASE_URL}/api/payment/verify`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
                planId,
              }),
            });

            if (verifyResponse.ok) {
              const verifyData = await verifyResponse.json();
              if (verifyData.success) {
                navigate("/dashboard");
                window.location.reload();
              } else {
                setError(verifyData.error || "Payment verification failed. Please contact support.");
              }
            } else {
              const errorData = await verifyResponse.json().catch(() => ({}));
              setError(errorData.error || "Payment verification failed. Please contact support.");
            }
          } catch (verifyError) {
            console.error("Payment verification error:", verifyError);
            setError("Payment verification failed. Please contact support if payment was deducted.");
          }
        },
        prefill: {
          email: user?.email || "",
        },
        theme: {
          color: "#2563eb",
        },
        modal: {
          ondismiss: function() {
            console.log("Payment modal closed");
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        setError(`Payment failed: ${response.error.description || response.error.reason || "Unknown error"}`);
      });
      
      razorpay.open();
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || "Failed to initiate payment. Please try again.");
    }
  };

  const formatLimit = (limit) => {
    if (limit === null || limit === undefined) return "Unlimited";
    return typeof limit === "number" ? limit : String(limit || "Unlimited");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
        py: 4,
      }}
    >
      {/* Background Effects */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(#1e3a8a 0.5px, transparent 0.5px),
            radial-gradient(#1e3a8a 0.5px, #ffffff 0.5px)
          `,
          backgroundSize: "40px 40px",
          backgroundPosition: "0 0, 20px 20px",
          opacity: 0.03,
        }}
      />

      <Container 
        maxWidth="xl" 
        sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          py: 4,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Back Button */}
        <Box sx={{ mb: 3, alignSelf: "flex-start", width: "100%" }}>
          <IconButton
            onClick={() => navigate("/")}
            sx={{
              color: "#1e3a8a",
              bgcolor: "rgba(30, 58, 138, 0.05)",
              "&:hover": {
                bgcolor: "rgba(30, 58, 138, 0.1)",
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        </Box>

        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 6, width: "100%" }}>
          <Typography
            variant="h2"
            sx={{
              color: "#1e3a8a",
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
            }}
          >
            Choose Your Plan
          </Typography>
          <Typography variant="h6" sx={{ color: "#64748b", fontWeight: 500 }}>
            Select the perfect plan for your needs
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              bgcolor: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #fee2e2",
            }}
            onClose={() => setError("")}
          >
            {(() => {
              if (typeof error === "string") return error;
              if (error?.message && typeof error.message === "string") return error.message;
              if (error?.error && typeof error.error === "string") return error.error;
              try {
                return JSON.stringify(error);
              } catch {
                return "An error occurred";
              }
            })()}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: "center", py: 8, width: "100%" }}>
            <Typography sx={{ color: "#1e3a8a" }}>Loading plans...</Typography>
          </Box>
        ) : (
          <Grid container spacing={4} justifyContent="center" sx={{ width: "100%", maxWidth: "1400px" }}>
            {plans.map((plan, index) => (
              <Grid item xs={12} sm={6} md={3} key={plan.id || index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  style={{ height: "100%" }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: "100%",
                      bgcolor: "#ffffff",
                      border: plan.id === "pro" ? "2px solid #1e3a8a" : "1px solid #e2e8f0",
                      borderRadius: 4,
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      },
                      ...(plan.id === "pro" && {
                        boxShadow: "0 10px 15px -3px rgba(30, 58, 138, 0.1)",
                      }),
                    }}
                  >
                    {plan.id === "pro" && (
                      <Chip
                        label="Popular"
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                          fontWeight: 700,
                          bgcolor: "#1e3a8a",
                          color: "white",
                          fontSize: "0.75rem",
                        }}
                      />
                    )}

                    <Typography
                      variant="h5"
                      sx={{ color: "#1e3a8a", fontWeight: 800, mb: 1 }}
                    >
                      {safeRender(plan.name, "Plan")}
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          color: "#0f172a",
                          fontWeight: 800,
                          display: "inline",
                        }}
                      >
                        ₹{(() => {
                          const price = plan.price;
                          if (typeof price === "number") return price;
                          if (typeof price === "string") {
                            const num = Number(price);
                            return isNaN(num) ? 0 : num;
                          }
                          return 0;
                        })()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#64748b", display: "inline", ml: 1, fontWeight: 600 }}
                      >
                        /month
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 3, borderColor: "#f1f5f9" }} />

                    <Box sx={{ flex: 1, mb: 3 }}>
                      {plan.limits && (
                        <>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Check sx={{ color: "#10b981", mr: 1.5, fontSize: "20px" }} />
                            <Typography sx={{ color: "#334155", fontWeight: 500, fontSize: "0.95rem" }}>
                              {plan.limits.dailyPrompts === null ? "Unlimited" : formatLimit(plan.limits.dailyPrompts || plan.limits.totalPrompts)} prompts{" "}
                              {plan.limits.dailyPrompts !== null ? "per day" : plan.limits.totalPrompts ? "total" : ""}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Check sx={{ color: "#10b981", mr: 1.5, fontSize: "20px" }} />
                            <Typography sx={{ color: "#334155", fontWeight: 500, fontSize: "0.95rem" }}>
                              {formatLimit(plan.limits.dailyImages)} images per day
                            </Typography>
                          </Box>
                          {plan.limits.maxUserLogins !== undefined && plan.limits.maxUserLogins !== null && (
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                              <Check sx={{ color: "#10b981", mr: 1.5, fontSize: "20px" }} />
                              <Typography sx={{ color: "#334155", fontWeight: 500, fontSize: "0.95rem" }}>
                                {formatLimit(plan.limits.maxUserLogins)} user login{plan.limits.maxUserLogins !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* General Plan Benefits */}
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Check sx={{ color: "#10b981", mr: 1.5, fontSize: "20px" }} />
                            <Typography sx={{ color: "#334155", fontWeight: 500, fontSize: "0.95rem" }}>
                              Multiple AI models
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Check sx={{ color: "#10b981", mr: 1.5, fontSize: "20px" }} />
                            <Typography sx={{ color: "#334155", fontWeight: 500, fontSize: "0.95rem" }}>
                              Chat history
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Check sx={{ color: "#10b981", mr: 1.5, fontSize: "20px" }} />
                            <Typography sx={{ color: "#334155", fontWeight: 500, fontSize: "0.95rem" }}>
                              Project folders
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleUpgrade(plan.id)}
                      sx={{
                        py: 1.5,
                        fontSize: "16px",
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: "12px",
                        bgcolor: plan.id === "pro" ? "#1e3a8a" : "#f1f5f9",
                        color: plan.id === "pro" ? "#ffffff" : "#1e3a8a",
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: plan.id === "pro" ? "#1e40af" : "#e2e8f0",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {plan.id === "free" ? "Get Started" : "Upgrade Now"}
                    </Button>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
